import { useEffect, useRef } from 'react';

/**
 * The one way this app polls.
 *
 * A timer that fires regardless of whether anyone is looking is a request
 * per tick per open tab, all day. The rules, so no caller has to remember them:
 *   - Nothing runs while the tab is hidden (bar the first, `immediate`, run —
 *     that is the page loading). A tick that came due in the background runs
 *     as soon as the tab is visible again.
 *   - Coming back to the tab (visible or focused) refreshes once right away,
 *     unless the last run is more recent than `focusMinGap`, or the person
 *     was away for less than `minAway`. A window losing and regaining focus
 *     every few seconds (another app, a remote-desktop or recording tool) is
 *     not someone coming back: on 26 Oct one such browser re-fetched a chat
 *     thread every 6 s and the inbox badge every 18 s for ten hours.
 *   - Delays grow from `interval` to `maxInterval` (doubling), so a long wait
 *     costs a handful of requests rather than one every couple of seconds.
 *     Coming back to the tab starts again from `interval`.
 *   - `fn` returning `false` stops polling (the thing reached a terminal
 *     state); a thrown error does not.
 *   - One run at a time; the next delay starts when the run finishes.
 *
 * `fn` receives `isActive()`, false once this poller has stopped — check it
 * before setting state from a response that may belong to an old target.
 */
const WAKE_MIN_GAP_MS = 1000; // visibilitychange and focus both fire on a tab switch

/**
 * Call `fn` when the person comes back to the tab after being away (tab hidden
 * or window blurred) for at least `minAway` ms, and no more often than every
 * `minGap` ms. Returns an unsubscribe function.
 *
 * `fn` receives how long they were away. The one place "coming back" is
 * decided, so a flickering window focus is ignored everywhere at once.
 */
export function onTabReturn(fn, { minAway = 0, minGap = WAKE_MIN_GAP_MS } = {}) {
  if (typeof document === 'undefined' || typeof window === 'undefined') return () => {};
  let awayAt = document.visibilityState === 'hidden' ? Date.now() : null;
  let lastFired = 0;

  const onAway = () => { if (awayAt == null) awayAt = Date.now(); };
  const onHidden = () => { if (document.visibilityState === 'hidden') onAway(); };
  const onBack = () => {
    if (document.visibilityState === 'hidden') return;
    const now = Date.now();
    const awayFor = awayAt == null ? 0 : now - awayAt;
    awayAt = null;
    if (awayFor < minAway) return;
    if (now - lastFired < Math.max(minGap, WAKE_MIN_GAP_MS)) return;
    lastFired = now;
    fn(awayFor);
  };

  document.addEventListener('visibilitychange', onHidden);
  document.addEventListener('visibilitychange', onBack);
  window.addEventListener('blur', onAway);
  window.addEventListener('focus', onBack);
  return () => {
    document.removeEventListener('visibilitychange', onHidden);
    document.removeEventListener('visibilitychange', onBack);
    window.removeEventListener('blur', onAway);
    window.removeEventListener('focus', onBack);
  };
}

export function startPolling(fn, {
  interval,
  maxInterval = interval,
  immediate = true,
  refreshOnFocus = true,
  focusMinGap = 0,
  minAway = 0,
} = {}) {
  let stopped = false;
  let timer = null;
  let inFlight = false;
  let due = false;
  let delay = interval;
  let lastRun = Date.now();

  const isHidden = () => typeof document !== 'undefined' && document.visibilityState === 'hidden';
  const isActive = () => !stopped;

  const schedule = () => {
    clearTimeout(timer);
    if (stopped) return;
    timer = setTimeout(onTimer, delay);
    delay = Math.min(delay * 2, Math.max(maxInterval, interval));
  };

  const run = async () => {
    if (stopped || inFlight) return;
    inFlight = true;
    due = false;
    lastRun = Date.now();
    clearTimeout(timer);
    timer = null;
    let result;
    try {
      result = await fn(isActive);
    } catch (_) {
      // A failed poll is still a poll: try again on the next tick.
    }
    inFlight = false;
    if (stopped) return;
    if (result === false) { stop(); return; }
    schedule();
  };

  function onTimer() {
    timer = null;
    if (isHidden()) { due = true; return; }
    run();
  }

  const onWake = (awayFor) => {
    if (stopped || inFlight || isHidden()) return;
    // A real return restarts the backoff; a focus flicker leaves it alone.
    const back = awayFor >= minAway;
    if (back) delay = interval;
    const since = Date.now() - lastRun;
    if (due || (back && refreshOnFocus && since >= Math.max(focusMinGap, WAKE_MIN_GAP_MS))) {
      if (refreshOnFocus) run();
      else { due = false; schedule(); }
    } else if (!timer) {
      schedule();
    }
  };

  // minGap 0: the timing rules (focusMinGap, due) are decided in onWake.
  const unsubscribeReturn = onTabReturn(onWake, { minGap: 0 });

  function stop() {
    stopped = true;
    clearTimeout(timer);
    timer = null;
    unsubscribeReturn();
  }

  // The first run is the page loading its data, not a poll: a tab opened in
  // the background should be ready when it is looked at.
  if (immediate) run();
  else schedule();

  return stop;
}

/**
 * @param {(isActive: () => boolean) => any} fn  return false to stop
 * @param {object}  opts
 * @param {number}  opts.interval          first delay, ms
 * @param {number}  [opts.maxInterval]     cap for the doubling delay (default: no backoff)
 * @param {boolean} [opts.enabled=true]    poll only while true — e.g. while something is in flight
 * @param {boolean} [opts.immediate=true]  run once on start
 * @param {boolean} [opts.refreshOnFocus=true] run when the tab comes back
 * @param {number}  [opts.focusMinGap=0]   skip that run if the last one is younger than this
 * @param {number}  [opts.minAway=0]       only count a return after being away this long, ms
 * @param {any}     [opts.key]             restart when this changes (a new target)
 */
export default function usePolling(fn, {
  enabled = true,
  interval,
  maxInterval,
  immediate = true,
  refreshOnFocus = true,
  focusMinGap = 0,
  minAway = 0,
  key,
} = {}) {
  const fnRef = useRef(fn);
  fnRef.current = fn;

  useEffect(() => {
    if (!enabled || !interval) return undefined;
    return startPolling((isActive) => fnRef.current(isActive), {
      interval, maxInterval, immediate, refreshOnFocus, focusMinGap, minAway,
    });
  }, [enabled, interval, maxInterval, immediate, refreshOnFocus, focusMinGap, minAway, key]);
}
